'use client';

import { Button } from '@common/components';
import { cn } from '@core/utils';
import type { ListChildcareRelatedPolicyDto } from '@generated/auth';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { AuthQueryOptions } from '@/api';
import {
  PolicyCard,
  type PolicyCardProps,
} from '@/components/molecule/PolicyCard';
import { useAuthStore } from '@/stores/AuthStore';

interface RelatedPolicyListProps {
  initialPolicyList: ListChildcareRelatedPolicyDto[];
  subRegionCode: string;
}

export const RelatedPolicyList = ({
  initialPolicyList,
  subRegionCode,
}: RelatedPolicyListProps) => {
  const [isOpened, setIsOpened] = useState(false);

  const isLogin = useAuthStore((state) => state.isLogin);

  const { data, isLoading } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/policy-case/baby/related-policy']({
      query: { subRegionCode: subRegionCode },
    }),
    enabled: isLogin === true,
    placeholderData: {
      context: {
        relatedPolicyList: initialPolicyList,
      },
    },
    select: ({ context }) =>
      context?.relatedPolicyList?.map(
        (policy) =>
          ({
            id: policy.id,
            idIdx: policy.metaPolicyIdIdx,
            imageUrl: policy.agencyLogo,
            name: policy.policyName,
            endAt: policy.expirationDate,
          }) satisfies PolicyCardProps,
      ),
  });

  const displayedPolicyList = isOpened ? data : data?.slice(0, 2);

  return (
    <section className="flex flex-col gap-20">
      <ul className="flex flex-col gap-12">
        {isLoading
          ? Array.from({ length: 2 }).map((_, index) => (
              <li key={index}>
                <PolicyCard border isLoading />
              </li>
            ))
          : displayedPolicyList?.map((policy) => (
              <li key={policy.id}>
                <PolicyCard {...policy} border />
              </li>
            ))}
      </ul>
      {isOpened ? null : (
        <Button
          className={cn('w-full', {
            hidden: (data?.length ?? 0) < 3,
          })}
          dimensions="H48"
          theme="sub-line"
          onClick={() => setIsOpened(true)}
        >
          더보기
        </Button>
      )}
    </section>
  );
};
